import { useQuery } from "@apollo/client";
import { BaseTable, Button, Flexbox, IBaseTableProps } from "@sede-x/shell-ds-react-framework";
import { loader } from "graphql.macro";
import { useState } from "react";
import { ApolloErrorViewer } from "shared/components/ApolloErrorViewer";
import InlineLoadingPanel from "shared/components/InlineLoadingPanel";
import {
  GqlResponse,
  TMovement,
  TMovementAlert,
  TMovementGroup
} from "ticketing/ticketing.types";

const QUERY_MOVEMENT_ALERTS = loader("../../ticketing-graphql/alertsOfMovement.graphql");

type Response = GqlResponse<Partial<TMovement[]>, "movements">;
type MovementAlertsProps = {
  movementGroup: TMovementGroup;
  onClose: () => void;
};

const transformData = (movements: Partial<TMovement[]>): TMovementAlert[] => {
  const alerts = movements.flatMap(m => m?.alerts ?? []);
  return [...new Map(alerts.map(item => [item.code, item])).values()];
};

export const MovementAlerts = ({ movementGroup, onClose }: MovementAlertsProps) => {
  const [movementAlerts, setMovementAlerts] = useState<TMovementAlert[]>();
  const { loading, error } = useQuery<Response>(QUERY_MOVEMENT_ALERTS, {
    fetchPolicy: "no-cache",
    variables: { ids: movementGroup.movements.map(m => m.id) },
    onCompleted: data => setMovementAlerts(transformData(data.movements))
  });

  const columns: IBaseTableProps<TMovementAlert>["columns"] = [
    {
      header: "Category",
      accessorKey: "category",
      size: 140
    },
    {
      header: "Code",
      accessorKey: "code",
      size: 100
    },
    {
      header: "Description",
      accessorKey: "desc",
      size: 300
    }
  ];
  if (loading) {
    return <InlineLoadingPanel />;
  }
  if (error) {
    return <ApolloErrorViewer error={error} />;
  }

  return (
    <Flexbox flexDirection="column" style={{ height: "100%" }}>
      {/* Toolbar */}
      <Flexbox
        justifyContent="flex-end"
        style={{
          borderBottom: "1px solid #e0e0e0",
          padding: "8px"
        }}>
        <Button size="small" sentiment="negative" onClick={onClose}>
          Close
        </Button>
      </Flexbox>

      <BaseTable data={movementAlerts} columns={columns} />
    </Flexbox>
  );
};
